import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarView } from './CalendarView.js';
import type { NoteEventPreset } from '../journal/DayNotes.js';
import { TasksView } from './TasksView.js';
import { SwipePages, neighbourIn } from '../../gestures/SwipeProvider.js';
import { SealedNotice } from '../SealedNotice.js';
import { useIsChild } from '../../hooks/useModules.js';
import { LetterReader } from './LetterReader.js';
import { PLAN_SCOPES } from '../../rooms.js';
import type { PlanScope } from '../../rooms.js';

export type PlanView = 'calendar' | 'tasks';

/**
 * Planen (E12): Kalender und Aufgaben. Eingerichtet (Abos verbinden) wird
 * hinter dem Einstellungen-Knopf. Der Elternbrief-Leser (27.10.2026) wohnt
 * hier, weil Familien hier planen — nur für Erwachsene.
 */
export function PlanScreen({
  onOpenTraining,
  onWriteNote,
  onRefreshed,
  initialView = 'calendar',
  focusTaskId = null,
  onConnectCalendar,
  initialScope = 'all',
}: {
  /**
   * Räume (27.10.2026): eine Planung über alle Räume, vorgefiltert — im Raum
   * Ich „meins“, im Haushalt „unseres“. Der Umschalter bleibt sichtbar.
   */
  initialScope?: PlanScope;
  onConnectCalendar?: (() => void) | undefined;
  onOpenTraining: () => void;
  /** „Eintrag dazu schreiben" — öffnet das Tagebuch mit diesem Termin (E14). */
  onWriteNote: (preset: NoteEventPreset) => void;
  onRefreshed: () => void;
  initialView?: PlanView;
  /** Aus einer Mitteilung: diese Aufgabe zeigen. */
  focusTaskId?: string | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [view, setView] = useState<PlanView>(initialView);
  const [focus, setFocus] = useState<string | null>(focusTaskId);
  const [letter, setLetter] = useState(false);
  const [scope, setScope] = useState<PlanScope>(initialScope);
  const child = useIsChild();
  const tabs: { key: PlanView; label: string }[] = [
    { key: 'calendar', label: t('plan.calendar') },
    { key: 'tasks', label: t('plan.tasks') },
  ];

  return (
    <div className="flex flex-col gap-3">
      <nav aria-label={t('plan.label')}>
        <ul className="flex gap-1 rounded-full border border-line bg-surface p-1">
          {tabs.map((tab) => (
            <li key={tab.key} className="flex-1">
              <button
                type="button"
                aria-current={view === tab.key ? 'page' : undefined}
                onClick={() => setView(tab.key)}
                className={`kw-btn kw-btn-small w-full px-1 ${
                  view === tab.key ? '' : 'border-0 bg-transparent text-ink-muted'
                }`}
              >
                {tab.label}
              </button>
            </li>
          ))}
        </ul>
      </nav>
      <div className="flex flex-col gap-1">
        <div role="group" aria-label={t('plan.scope.label')} className="flex gap-1">
          {PLAN_SCOPES.map((entry) => (
            <button
              key={entry}
              type="button"
              aria-pressed={scope === entry}
              onClick={() => setScope(entry)}
              className="kw-toggle flex-1"
            >
              {t(SCOPE_LABEL[entry])}
            </button>
          ))}
        </div>
        <p className="text-xs text-ink-muted">{t(SCOPE_HINT[scope])}</p>
      </div>
      <SealedNotice id="plan-sealed" />
      {letter ? (
        <LetterReader onClose={() => setLetter(false)} />
      ) : (
        <>
          {child ? null : (
            <button
              type="button"
              onClick={() => setLetter(true)}
              className="kw-btn kw-btn-quiet kw-btn-small"
            >
              {t('letter.open')}
            </button>
          )}
          {/* Wischen auf dem Inhalt wechselt die Unterreiter (24.10.2026). */}
          <SwipePages
            current={view}
            neighbour={neighbourIn(tabs.map((tab) => tab.key))}
            onChange={setView}
            render={(key) =>
              key === 'calendar' ? (
                <CalendarView
                  onOpenTraining={onOpenTraining}
                  onWriteNote={onWriteNote}
                  onOpenTask={(id) => {
                    setFocus(id);
                    setView('tasks');
                  }}
                  onRefreshed={onRefreshed}
                  onConnectCalendar={onConnectCalendar}
                  scope={scope}
                />
              ) : (
                <TasksView focusTaskId={focus} scope={scope} />
              )
            }
          />
        </>
      )}
    </div>
  );
}

const SCOPE_LABEL = {
  mine: 'plan.scope.mine',
  ours: 'plan.scope.ours',
  all: 'plan.scope.all',
} as const satisfies Record<PlanScope, string>;

const SCOPE_HINT = {
  mine: 'plan.scope.hintMine',
  ours: 'plan.scope.hintOurs',
  all: 'plan.scope.hintAll',
} as const satisfies Record<PlanScope, string>;
